import React, { ReactElement } from 'react';
import QuestionSchema from '@ax2/common/model/schemas/QuestionSchema';
import QuestionType from '@ax2/common/model/QuestionType';
import { EmptyQuestion } from 'components/question-builders/EmptyQuestion';
import { QuestionChangeData } from 'components/question-builders/QuestionContent';
import { OpenEndedTextQuestion } from 'components/question-builders/OpenEndedTextQuestion';
import {
  getGridQuestionData,
  getListQuestionData,
  getTextQuestionData,
  getRatingListQuestionData,
  getEmptyQuestionData,
  getTextListQuestionData,
} from './questionDataConverter';
import TextQuestionSchema from '@ax2/common/model/schemas/TextQuestionSchema';
import {
  CheckboxGridQuestion,
  CheckboxGridQuestionData,
} from 'components/question-builders/CheckboxGridQuestion';
import GridQuestionSchema from '@ax2/common/model/schemas/GridQuestionSchema';
import {
  RadioButtonGridQuestion,
  RadioButtonGridQuestionData,
} from 'components/question-builders/RadioButtonGridQuestion';
import {
  RadioButtonGridRowQuestion,
  RadioButtonGridRowQuestionData,
} from 'components/question-builders/RadioButtonGridRowQuestion';
import {
  CheckboxListQuestion,
  CheckboxListQuestionData,
} from 'components/question-builders/CheckboxListQuestion';
import ListQuestionSchema from '@ax2/common/model/schemas/ListQuestionSchema';
import {
  RadioButtonListQuestion,
  RadioButtonListQuestionData,
} from 'components/question-builders/RadioButtonListQuestion';

import {
  TextboxListTextQuestion,
  TextboxListTextQuestionData,
} from 'components/question-builders/TextboxListTextQuestion';
import {
  TextboxListNumericQuestion,
  TextboxListNumericQuestionData,
} from 'components/question-builders/TextboxListNumericQuestion';
import {
  TextboxGridNumericQuestion,
  TextboxGridNumericQuestionData,
} from 'components/question-builders/TextboxGridNumericQuestion';
import {
  TextboxListSumQuestion,
  TextboxListSumData,
} from '../components/question-builders/TextboxListSumQuestion';
import { OpenEndedNumericQuestion } from 'components/question-builders/OpenEndedNumericQuestion';
import {
  DropdownMenuQuestion,
  DropdownMenuQuestionData,
} from 'components/question-builders/DropdownMenuQuestion';
import {
  RatingListQuestion,
  RatingListQuestionData,
} from 'components/question-builders/RatingListQuestion';
import EmptyQuestionSchema from '@ax2/common/model/schemas/EmptyQuestionSchema';
import {
  RankingListQuestion,
  RankingListQuestionData,
} from 'components/question-builders/RankingListQuestion';

export function getQuestion(
  question: QuestionSchema,
  isScreener: boolean,
  onChange: (s: QuestionSchema) => void,
  qChangeData: QuestionChangeData
): ReactElement {
  switch (question.questionType) {
    case QuestionType.Empty:
      return React.createElement(
        EmptyQuestion,
        getEmptyQuestionData(question as EmptyQuestionSchema, qChangeData)
      );
    case QuestionType.OpenEndedText:
      return React.createElement(
        OpenEndedTextQuestion,
        getTextQuestionData(
          question as TextQuestionSchema,
          isScreener,
          onChange,
          qChangeData
        )
      );
    case QuestionType.OpenEndedNumeric:
      return React.createElement(
        OpenEndedNumericQuestion,
        getTextQuestionData(
          question as TextQuestionSchema,
          isScreener,
          onChange,
          qChangeData
        )
      );
    case QuestionType.CheckboxGrid:
      return React.createElement(CheckboxGridQuestion, getGridQuestionData(
        question as GridQuestionSchema,
        isScreener,
        onChange,
        qChangeData
      ) as CheckboxGridQuestionData);
    case QuestionType.RatingList:
      return React.createElement(RatingListQuestion, getRatingListQuestionData(
        question as GridQuestionSchema,
        isScreener,
        onChange,
        qChangeData
      ) as RatingListQuestionData);
    case QuestionType.RadioButtonGrid:
      return React.createElement(RadioButtonGridQuestion, getGridQuestionData(
        question as GridQuestionSchema,
        isScreener,
        onChange,
        qChangeData
      ) as RadioButtonGridQuestionData);
    case QuestionType.RadioButtonGridRow:
      return React.createElement(
        RadioButtonGridRowQuestion,
        getGridQuestionData(
          question as GridQuestionSchema,
          isScreener,
          onChange,
          qChangeData
        ) as RadioButtonGridRowQuestionData
      );

    case QuestionType.DropdownMenu:
      return React.createElement(DropdownMenuQuestion, getListQuestionData(
        question as ListQuestionSchema,
        isScreener,
        onChange,
        qChangeData
      ) as DropdownMenuQuestionData);
    case QuestionType.CheckboxList:
      return React.createElement(CheckboxListQuestion, getListQuestionData(
        question as ListQuestionSchema,
        isScreener,
        onChange,
        qChangeData
      ) as CheckboxListQuestionData);
    case QuestionType.RadioButtonList:
      return React.createElement(RadioButtonListQuestion, getListQuestionData(
        question as ListQuestionSchema,
        isScreener,
        onChange,
        qChangeData
      ) as RadioButtonListQuestionData);
    case QuestionType.TextboxListText:
      return React.createElement(
        TextboxListTextQuestion,
        getTextListQuestionData(
          question as ListQuestionSchema,
          isScreener,
          onChange,
          qChangeData
        ) as TextboxListTextQuestionData
      );
    case QuestionType.TextboxGridNumeric:
      return React.createElement(
        TextboxGridNumericQuestion,
        getGridQuestionData(
          question as GridQuestionSchema,
          isScreener,
          onChange,
          qChangeData
        ) as TextboxGridNumericQuestionData
      );
    case QuestionType.TextboxListNumeric:
      return React.createElement(
        TextboxListNumericQuestion,
        getTextListQuestionData(
          question as ListQuestionSchema,
          isScreener,
          onChange,
          qChangeData
        ) as TextboxListNumericQuestionData
      );
    case QuestionType.ContinuousSumList:
      return React.createElement(TextboxListSumQuestion, getListQuestionData(
        question as ListQuestionSchema,
        isScreener,
        onChange,
        qChangeData
      ) as TextboxListSumData);
    case QuestionType.RankingList:
      return React.createElement(RankingListQuestion, getListQuestionData(
        question as ListQuestionSchema,
        isScreener,
        onChange,
        qChangeData
      ) as RankingListQuestionData);
    default:
      throw Error(`Unknown QuestionType was used in question builder.`);
  }
}
